import { z } from "zod";

/* ------------------------------------------------------------------ media */

/**
 * A slot holds a photograph or a video loop. The brief allows a video hero but
 * requires a poster frame with it, so `posterUrl` is what renders until the
 * video can play (and permanently under `prefers-reduced-motion`).
 */
export const MEDIA_KINDS = ["image", "video"] as const;
export const mediaKindSchema = z.enum(MEDIA_KINDS);
export type MediaKind = z.infer<typeof mediaKindSchema>;

export const mediaAssetSchema = z.object({
  id: z.string().uuid(),
  kind: mediaKindSchema,
  filename: z.string(),
  url: z.string(),
  posterUrl: z.string().nullable(),
  alt: z.string().nullable(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
  sizeBytes: z.number().int().nullable(),
  durationSeconds: z.number().nullable(),
  tags: z.array(z.string()),
  createdAt: z.string(),
});
export type MediaAsset = z.infer<typeof mediaAssetSchema>;

/** The four fields every content DTO exposes for its media slot. */
export const mediaSlotSchema = z.object({
  imageUrl: z.string().nullable(),
  imageAlt: z.string().nullable(),
  imageKind: mediaKindSchema,
  imagePosterUrl: z.string().nullable(),
});
export type MediaSlotFields = z.infer<typeof mediaSlotSchema>;

/* ------------------------------------------------------------------- home */

export const nearbyPlaceSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  description: z.string(),
  driveMinutes: z.number().int(),
  imageUrl: z.string().nullable(),
  imageAlt: z.string().nullable(),
  imageKind: mediaKindSchema,
  imagePosterUrl: z.string().nullable(),
  position: z.number().int(),
});
export type NearbyPlace = z.infer<typeof nearbyPlaceSchema>;

export const villaStatSchema = z.object({
  id: z.string().uuid(),
  value: z.string(),
  unit: z.string().nullable(),
  label: z.string(),
  position: z.number().int(),
});
export type VillaStat = z.infer<typeof villaStatSchema>;

/* ------------------------------------------------------------------ villa */

export const IMAGE_POSITIONS = ["left", "right"] as const;
export const imagePositionSchema = z.enum(IMAGE_POSITIONS);
export type ImagePosition = z.infer<typeof imagePositionSchema>;

export const villaSpaceSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  description: z.string(),
  specs: z.array(z.string()),
  imageUrl: z.string().nullable(),
  imageAlt: z.string().nullable(),
  imageKind: mediaKindSchema,
  imagePosterUrl: z.string().nullable(),
  imagePosition: imagePositionSchema,
  position: z.number().int(),
});
export type VillaSpace = z.infer<typeof villaSpaceSchema>;

export const amenitySchema = z.object({
  id: z.string().uuid(),
  category: z.string(),
  label: z.string(),
  icon: z.string(),
  position: z.number().int(),
});
export type Amenity = z.infer<typeof amenitySchema>;

export const houseRuleSchema = z.object({
  id: z.string().uuid(),
  text: z.string(),
  emphasis: z.string().nullable(),
  highlighted: z.boolean(),
  position: z.number().int(),
});
export type HouseRule = z.infer<typeof houseRuleSchema>;

export const rateSchema = z.object({
  id: z.string().uuid(),
  label: z.string(),
  value: z.string(),
  position: z.number().int(),
});
export type Rate = z.infer<typeof rateSchema>;

/* ------------------------------------------------------------------ story */

export const CHAPTER_LAYOUTS = ["image-left", "image-right", "full-bleed", "text-only"] as const;
export const chapterLayoutSchema = z.enum(CHAPTER_LAYOUTS);
export type ChapterLayout = z.infer<typeof chapterLayoutSchema>;

export const storyChapterSchema = z.object({
  id: z.string().uuid(),
  eyebrow: z.string().nullable(),
  title: z.string().nullable(),
  body: z.array(z.string()),
  pullQuote: z.string().nullable(),
  layout: chapterLayoutSchema,
  imageUrl: z.string().nullable(),
  imageAlt: z.string().nullable(),
  imageKind: mediaKindSchema,
  imagePosterUrl: z.string().nullable(),
  position: z.number().int(),
});
export type StoryChapter = z.infer<typeof storyChapterSchema>;

export const artworkSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  caption: z.string(),
  imageUrl: z.string().nullable(),
  imageAlt: z.string().nullable(),
  imageKind: mediaKindSchema,
  imagePosterUrl: z.string().nullable(),
  position: z.number().int(),
});
export type Artwork = z.infer<typeof artworkSchema>;

export const etiquetteSchema = z.object({
  title: z.string(),
  points: z.array(z.string()),
  linkLabel: z.string().nullable(),
  linkUrl: z.string().nullable(),
});
export type Etiquette = z.infer<typeof etiquetteSchema>;

/* ------------------------------------------------------------ experiences */

export const EXPERIENCE_KINDS = ["item", "break"] as const;
export const experienceKindSchema = z.enum(EXPERIENCE_KINDS);
export type ExperienceKind = z.infer<typeof experienceKindSchema>;

export const experienceSchema = z.object({
  id: z.string().uuid(),
  kind: experienceKindSchema,
  title: z.string().nullable(),
  description: z.string().nullable(),
  driveLabel: z.string().nullable(),
  tip: z.string().nullable(),
  quote: z.string().nullable(),
  imageUrl: z.string().nullable(),
  imageAlt: z.string().nullable(),
  imageKind: mediaKindSchema,
  imagePosterUrl: z.string().nullable(),
  imagePosition: imagePositionSchema,
  position: z.number().int(),
});
export type Experience = z.infer<typeof experienceSchema>;

export const nearbyRowSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  driveLabel: z.string(),
  position: z.number().int(),
});
export type NearbyRow = z.infer<typeof nearbyRowSchema>;

/* ---------------------------------------------------------------- gallery */

export const GALLERY_RATIOS = ["4/5", "1/1", "3/4", "16/10"] as const;
export const galleryRatioSchema = z.enum(GALLERY_RATIOS);
export type GalleryRatio = z.infer<typeof galleryRatioSchema>;

export const galleryItemSchema = z.object({
  id: z.string().uuid(),
  caption: z.string(),
  category: z.string(),
  ratio: galleryRatioSchema,
  imageUrl: z.string().nullable(),
  imageAlt: z.string().nullable(),
  imageKind: mediaKindSchema,
  imagePosterUrl: z.string().nullable(),
  position: z.number().int(),
});
export type GalleryItem = z.infer<typeof galleryItemSchema>;

export const galleryCategorySchema = z.object({
  slug: z.string(),
  label: z.string(),
  count: z.number().int(),
});
export type GalleryCategory = z.infer<typeof galleryCategorySchema>;

/* -------------------------------------------------------------------- faq */

export const faqSchema = z.object({
  id: z.string().uuid(),
  question: z.string(),
  answer: z.string(),
  position: z.number().int(),
});
export type Faq = z.infer<typeof faqSchema>;

/* ------------------------------------------------------------------ legal */

export const legalDocumentSchema = z.object({
  slug: z.string(),
  title: z.string(),
  sections: z.array(
    z.object({
      heading: z.string(),
      paragraphs: z.array(z.string()),
    }),
  ),
  updatedAt: z.string(),
});
export type LegalDocument = z.infer<typeof legalDocumentSchema>;

/* ------------------------------------------------------------------ pages */

export const PAGE_STATUSES = ["published", "draft"] as const;
export const pageStatusSchema = z.enum(PAGE_STATUSES);
export type PageStatus = z.infer<typeof pageStatusSchema>;

export const pageSchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  title: z.string(),
  path: z.string(),
  status: pageStatusSchema,
  metaTitle: z.string().nullable(),
  metaDescription: z.string().nullable(),
  updatedAt: z.string(),
});
export type Page = z.infer<typeof pageSchema>;

export const pageUpdateSchema = z.object({
  title: z.string().min(1).max(120).optional(),
  status: pageStatusSchema.optional(),
  metaTitle: z.string().max(200).nullable().optional(),
  metaDescription: z.string().max(400).nullable().optional(),
});
export type PageUpdateInput = z.infer<typeof pageUpdateSchema>;

/* -------------------------------------------------------- page payloads */

export const homeContentSchema = z.object({
  hero: z.object({
    eyebrow: z.string(),
    title: z.string(),
    lead: z.string(),
    ctaLabel: z.string(),
    imageUrl: z.string().nullable(),
    imageAlt: z.string().nullable(),
    imageKind: mediaKindSchema,
    imagePosterUrl: z.string().nullable(),
  }),
  intro: z.object({
    eyebrow: z.string(),
    body: z.string(),
    linkLabel: z.string(),
  }),
  villa: z.object({
    eyebrow: z.string(),
    title: z.string(),
    body: z.string(),
    imageUrl: z.string().nullable(),
    imageAlt: z.string().nullable(),
    imageKind: mediaKindSchema,
    imagePosterUrl: z.string().nullable(),
    stats: z.array(villaStatSchema),
  }),
  nearby: z.object({
    eyebrow: z.string(),
    title: z.string(),
    note: z.string(),
    items: z.array(nearbyPlaceSchema),
  }),
  location: z.object({
    eyebrow: z.string(),
    title: z.string(),
    body: z.string(),
    mapUrl: z.string(),
  }),
  cta: z.object({
    eyebrow: z.string(),
    title: z.string(),
    ctaLabel: z.string(),
  }),
});
export type HomeContent = z.infer<typeof homeContentSchema>;

const headingSchema = z.object({ eyebrow: z.string(), title: z.string() });

export const villaContentSchema = z.object({
  intro: z.object({
    eyebrow: z.string(),
    title: z.string(),
    lead: z.string(),
    heroImageUrl: z.string().nullable(),
    heroImageAlt: z.string().nullable(),
    heroImageKind: mediaKindSchema,
    heroImagePosterUrl: z.string().nullable(),
  }),
  spacesHeading: headingSchema,
  spaces: z.array(villaSpaceSchema),
  amenitiesHeading: headingSchema,
  amenities: z.array(amenitySchema),
  ratesTitle: z.string(),
  rates: z.array(rateSchema),
  ratesNote: z.string(),
  rulesTitle: z.string(),
  rules: z.array(houseRuleSchema),
});
export type VillaContent = z.infer<typeof villaContentSchema>;

export const storyContentSchema = z.object({
  intro: z.object({
    eyebrow: z.string(),
    title: z.string(),
    lead: z.string(),
  }),
  chapters: z.array(storyChapterSchema),
  artworks: z.array(artworkSchema),
  etiquette: etiquetteSchema,
});
export type StoryContent = z.infer<typeof storyContentSchema>;

export const experiencesContentSchema = z.object({
  intro: z.object({
    eyebrow: z.string(),
    title: z.string(),
    lead: z.string(),
  }),
  items: z.array(experienceSchema),
  nearbyTable: z.array(nearbyRowSchema),
});
export type ExperiencesContent = z.infer<typeof experiencesContentSchema>;

export const galleryContentSchema = z.object({
  title: z.string(),
  categories: z.array(galleryCategorySchema),
  items: z.array(galleryItemSchema),
});
export type GalleryContent = z.infer<typeof galleryContentSchema>;
